import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Alert, Autocomplete, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HistoryIcon from '@mui/icons-material/History';
import BlockIcon from '@mui/icons-material/Block';
import { httpsCallable } from 'firebase/functions';
import { getDocs, updateDoc } from 'firebase/firestore';
import { functions } from '../../../firebase';
import { sortPlayers } from '../../../utils/sorting';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import { seasonClubsCollection, seasonPlayersCollection, seasonPlayerDoc } from '../../../services/players';
import { fetchAuctionPlayerIdsForSeason } from '../../../services/auctions';
import { isNotEligiblePlayer } from '../../../constants/game';

const POSITION_KEYS = {
    Goalkeeper: 'keepers',
    Defender: 'verdedigers',
    Midfielder: 'middenvelders',
    Attacker: 'aanvallers',
};

function formatUpdated(value) {
    if (!value) return '—';
    const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    const dagen = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (dagen === 0) return 'vandaag';
    if (dagen === 1) return 'gisteren';
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

/**
 * De stamgegevens van één seizoen: welke clubs meedoen en wie er in hun selectie zit.
 *
 * Twee acties in plaats van de zeven importknoppen die hier stonden. Een club komt
 * binnen via "Club toevoegen" — dat schrijft de club én zijn selectie weg — en
 * "Synchroniseren" haalt de selecties van alle clubs in dit seizoen opnieuw op.
 * Het seizoen komt uit de route.
 */
function AdminStamgegevens() {
    const { seasonId, seasonName, isLocked } = useSeason();
    const notify = useNotification();

    const [clubs, setClubs] = useState([]);
    const [players, setPlayers] = useState([]);
    const [loadingClubs, setLoadingClubs] = useState(true);
    const [syncing, setSyncing] = useState(false);
    // Positiewijzigingen die een sync zou doorvoeren; null = pop-up dicht.
    const [positionChanges, setPositionChanges] = useState(null);
    const [acceptedChanges, setAcceptedChanges] = useState(new Set());
    const [removing, setRemoving] = useState(false);
    const [aggregating, setAggregating] = useState(false);

    const [search, setSearch] = useState('');
    const [selectedClubs, setSelectedClubs] = useState(new Set());
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

    // Spelers die in dit seizoen aan een veiling (subleague) zijn toegevoegd, plus
    // een filter om alleen die spelers te tonen. Daarvan is het namelijk belangrijk
    // dat de wedstrijden/goals/assists met de hand ingevuld worden.
    const [inAuctionIds, setInAuctionIds] = useState(new Set());
    const [filterInAuction, setFilterInAuction] = useState(false);

    // Club toevoegen
    const [addClubOpen, setAddClubOpen] = useState(false);
    const [sportmonksTeams, setSportmonksTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [hasFetchedTeams, setHasFetchedTeams] = useState(false);
    const [addingClub, setAddingClub] = useState(false);
    const [addClubError, setAddClubError] = useState(null);

    // Selectie (inline uitklapbaar per club, één tegelijk)
    const [expandedClubId, setExpandedClubId] = useState(null);
    const [squad, setSquad] = useState([]);
    const [playerStats, setPlayerStats] = useState({});
    const [loadingStats, setLoadingStats] = useState(false);
    const [playerToDelete, setPlayerToDelete] = useState(null);
    const debounceTimers = useRef({});

    // Foto bewerken
    const [photoOpen, setPhotoOpen] = useState(false);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [newPhotoUrl, setNewPhotoUrl] = useState('');

    // Speelgerechtigdheid
    const [eligibilityPlayer, setEligibilityPlayer] = useState(null);
    const [savingEligibility, setSavingEligibility] = useState(false);

    // Coach wisselen
    const [coachSwapOpen, setCoachSwapOpen] = useState(false);
    const [coachOut, setCoachOut] = useState(null);
    const [coachIn, setCoachIn] = useState(null);
    const [swappingCoach, setSwappingCoach] = useState(false);

    useEffect(() => {
        const timers = debounceTimers.current;
        return () => Object.values(timers).forEach(clearTimeout);
    }, []);

    const loadClubsAndPlayers = useCallback(async () => {
        if (!seasonId) return;

        setLoadingClubs(true);
        try {
            const [clubsSnap, playersSnap, auctionPlayerIds] = await Promise.all([
                getDocs(seasonClubsCollection(seasonId)),
                getDocs(seasonPlayersCollection(seasonId)),
                fetchAuctionPlayerIdsForSeason(seasonId),
            ]);

            setInAuctionIds(auctionPlayerIds);

            const alleSpelers = playersSnap.docs.map((d) => ({ ...d.data(), id: d.id }));
            setPlayers(alleSpelers);

            // Tellingen per club, zodat een club zonder selectie ook zichtbaar blijft.
            const perClub = new Map();
            alleSpelers.forEach((speler) => {
                if (speler.clubId == null) return;
                const key = String(speler.clubId);
                if (!perClub.has(key)) {
                    perClub.set(key, { coach: null, keepers: 0, verdedigers: 0, middenvelders: 0, aanvallers: 0, totaal: 0 });
                }
                const telling = perClub.get(key);
                telling.totaal++;
                if (speler.position === 'Coach') {
                    telling.coach = speler.name;
                } else if (POSITION_KEYS[speler.position]) {
                    telling[POSITION_KEYS[speler.position]]++;
                }
            });

            setClubs(clubsSnap.docs
                .map((d) => {
                    const data = d.data();
                    const telling = perClub.get(d.id) || { coach: null, keepers: 0, verdedigers: 0, middenvelders: 0, aanvallers: 0, totaal: 0 };
                    return { id: d.id, name: data.name, logoUrl: data.logoUrl, lastUpdated: data.lastUpdated, ...telling };
                })
                .sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            console.error('Kon de clubs niet ophalen:', err);
            notify.error('De clubs van dit seizoen konden niet opgehaald worden.');
        } finally {
            setLoadingClubs(false);
        }
    }, [seasonId, notify]);

    useEffect(() => { loadClubsAndPlayers(); }, [loadClubsAndPlayers]);

    const coaches = useMemo(
        () => players.filter((p) => p.position === 'Coach').sort((a, b) => a.name.localeCompare(b.name)),
        [players],
    );

    const zichtbareClubs = useMemo(() => {
        const term = search.trim().toLowerCase();

        let resultaat = clubs;
        if (term) {
            const clubIdsMetSpeler = new Set(
                players.filter((p) => (p.name || '').toLowerCase().includes(term)).map((p) => String(p.clubId)),
            );
            resultaat = resultaat.filter((c) => c.name.toLowerCase().includes(term) || clubIdsMetSpeler.has(c.id));
        }

        if (filterInAuction) {
            const clubIdsInVeiling = new Set(
                players.filter((p) => inAuctionIds.has(String(p.id))).map((p) => String(p.clubId)),
            );
            resultaat = resultaat.filter((c) => clubIdsInVeiling.has(c.id));
        }

        return resultaat;
    }, [clubs, players, search, filterInAuction, inAuctionIds]);

    const totaalSpelers = players.length;
    const laatstBijgewerkt = useMemo(() => {
        const stamps = clubs.map((c) => c.lastUpdated).filter(Boolean);
        if (stamps.length === 0) return null;
        return stamps.reduce((nieuwste, s) => (s.seconds > nieuwste.seconds ? s : nieuwste));
    }, [clubs]);

    const runSync = async (acceptedPositionChanges) => {
        setSyncing(true);
        try {
            const syncSeasonSquads = httpsCallable(functions, 'syncSeasonSquads');
            const result = await syncSeasonSquads({ seasonId, acceptedPositionChanges });
            notify.success(result.data.message);
            loadClubsAndPlayers();
        } catch (err) {
            console.error('Synchroniseren mislukt:', err);
            notify.error(err.message || 'Het synchroniseren is mislukt.');
        } finally {
            setSyncing(false);
        }
    };

    // Eerst kijken welke spelers een andere positie zouden krijgen. Zijn die er,
    // dan beslist de beheerder per speler in de pop-up; zonder vinkje blijft de
    // oude positie staan.
    const handleSync = async () => {
        setSyncing(true);
        try {
            const previewSeasonPositionChanges = httpsCallable(functions, 'previewSeasonPositionChanges');
            const result = await previewSeasonPositionChanges({ seasonId });
            const { changes, mislukt } = result.data;
            if (mislukt.length > 0) {
                notify.error(`Positiecontrole mislukt voor: ${mislukt.join(', ')}. Hun posities blijven ongewijzigd.`);
            }
            if (changes.length === 0) {
                await runSync([]);
                return;
            }
            setPositionChanges(changes);
            setAcceptedChanges(new Set());
            setSyncing(false);
        } catch (err) {
            console.error('Positiecontrole mislukt:', err);
            notify.error(err.message || 'De positiecontrole is mislukt.');
            setSyncing(false);
        }
    };

    const handleConfirmPositionChanges = () => {
        const accepted = Array.from(acceptedChanges);
        setPositionChanges(null);
        runSync(accepted);
    };

    const toggleAcceptedChange = (playerId) => {
        setAcceptedChanges((huidig) => {
            const volgende = new Set(huidig);
            if (volgende.has(playerId)) volgende.delete(playerId); else volgende.add(playerId);
            return volgende;
        });
    };

    const handleAggregateHistory = async () => {
        setAggregating(true);
        try {
            const aggregateSeasonPlayerTotals = httpsCallable(functions, 'aggregateSeasonPlayerTotals');
            const result = await aggregateSeasonPlayerTotals({ seasonId });
            notify.success(result.data.message);
        } catch (err) {
            console.error('Seizoenstotalen opslaan mislukt:', err);
            notify.error(err.message || 'De seizoenstotalen konden niet opgeslagen worden.');
        } finally {
            setAggregating(false);
        }
    };

    const fetchSportmonksTeams = async () => {
        if (hasFetchedTeams || loadingTeams) return;

        setLoadingTeams(true);
        try {
            const getAllTeams = httpsCallable(functions, 'getAllTeams');
            const result = await getAllTeams();
            setSportmonksTeams([...result.data].sort((a, b) => a.name.localeCompare(b.name)));
            setHasFetchedTeams(true);
        } catch (err) {
            console.error('Kon de clublijst niet ophalen:', err);
            setAddClubError('De clublijst van Sportmonks kon niet opgehaald worden.');
        } finally {
            setLoadingTeams(false);
        }
    };

    const handleAddClub = async () => {
        if (!selectedTeam) return;

        setAddingClub(true);
        setAddClubError(null);
        try {
            const addClubToSeason = httpsCallable(functions, 'addClubToSeason');
            const result = await addClubToSeason({ seasonId, teamId: selectedTeam.id });
            notify.success(result.data.message);
            setAddClubOpen(false);
            setSelectedTeam(null);
            loadClubsAndPlayers();
        } catch (err) {
            console.error('Club toevoegen mislukt:', err);
            // Dialoog blijft open zodat de fout leesbaar is.
            setAddClubError(err.message || 'De club kon niet toegevoegd worden.');
        } finally {
            setAddingClub(false);
        }
    };

    const handleRemoveClubs = async () => {
        setConfirmRemoveOpen(false);
        const clubIds = Array.from(selectedClubs);
        if (clubIds.length === 0) return;

        setRemoving(true);
        try {
            const removeClubFromSeason = httpsCallable(functions, 'removeClubFromSeason');
            const result = await removeClubFromSeason({ seasonId, clubIds });
            notify.success(result.data.message);
            setSelectedClubs(new Set());
            loadClubsAndPlayers();
        } catch (err) {
            console.error('Verwijderen mislukt:', err);
            notify.error(err.message || 'De clubs konden niet verwijderd worden.');
        } finally {
            setRemoving(false);
        }
    };

    const toggleClub = (clubId) => {
        setSelectedClubs((huidig) => {
            const volgende = new Set(huidig);
            if (volgende.has(clubId)) volgende.delete(clubId); else volgende.add(clubId);
            return volgende;
        });
    };

    const toggleSquad = async (club) => {
        // Nog een keer op dezelfde club klikken klapt hem weer dicht.
        if (expandedClubId === club.id) {
            setExpandedClubId(null);
            return;
        }

        setExpandedClubId(club.id);
        const selectie = sortPlayers(players.filter((p) => String(p.clubId) === club.id));
        setSquad(selectie);

        setLoadingStats(true);
        try {
            const playerIds = selectie.map((p) => String(p.id));
            if (playerIds.length === 0) {
                setPlayerStats({});
                return;
            }
            const getPlayerStats = httpsCallable(functions, 'getPlayerStats');
            const result = await getPlayerStats({ playerIds, seasonId });
            setPlayerStats(result.data);
        } catch (err) {
            console.error('Kon statistieken niet ophalen:', err);
            notify.error('De statistieken konden niet geladen worden.');
        } finally {
            setLoadingStats(false);
        }
    };

    const savePlayerStat = async (playerId, statsData) => {
        try {
            const updatePlayerStats = httpsCallable(functions, 'updatePlayerStats');
            await updatePlayerStats({ playerId, statsData, seasonId });
        } catch (err) {
            console.error(`Kon statistieken van speler ${playerId} niet opslaan:`, err);
            notify.error('De statistieken konden niet opgeslagen worden.');
        }
    };

    const handleStatChange = (playerId, field, value) => {
        const numeriek = value === '' ? 0 : Number(value);
        if (Number.isNaN(numeriek) || numeriek < 0) return;

        const voorSpeler = { ...(playerStats[playerId] || {}), playerId, [field]: numeriek };
        setPlayerStats((huidig) => ({ ...huidig, [playerId]: voorSpeler }));

        clearTimeout(debounceTimers.current[playerId]);
        debounceTimers.current[playerId] = setTimeout(() => savePlayerStat(playerId, voorSpeler), 1000);
    };

    const handleDeletePlayer = async () => {
        if (!playerToDelete) return;
        try {
            const deletePlayers = httpsCallable(functions, 'deletePlayers');
            await deletePlayers({ playerIds: [playerToDelete.id], seasonId });

            setSquad((huidig) => huidig.filter((p) => p.id !== playerToDelete.id));
            setPlayers((huidig) => huidig.filter((p) => p.id !== playerToDelete.id));
            notify.success(`${playerToDelete.name} is verwijderd.`);
            loadClubsAndPlayers();
        } catch (err) {
            console.error('Kon speler niet verwijderen:', err);
            notify.error('De speler kon niet verwijderd worden: ' + err.message);
        } finally {
            setPlayerToDelete(null);
        }
    };

    const handleToggleEligibility = async () => {
        if (!eligibilityPlayer) return;
        const notEligible = !isNotEligiblePlayer(eligibilityPlayer);
        setSavingEligibility(true);
        try {
            const setPlayerEligibility = httpsCallable(functions, 'setPlayerEligibility');
            const result = await setPlayerEligibility({ seasonId, playerId: String(eligibilityPlayer.id), notEligible });

            const bijwerken = (p) => (p.id === eligibilityPlayer.id ? { ...p, notEligible } : p);
            setSquad((huidig) => huidig.map(bijwerken));
            setPlayers((huidig) => huidig.map(bijwerken));
            notify.success(result.data.message);
            if (result.data.removedFrom > 0) {
                setInAuctionIds(await fetchAuctionPlayerIdsForSeason(seasonId));
            }
        } catch (err) {
            console.error('Kon de speelgerechtigdheid niet aanpassen:', err);
            notify.error(err.message || 'De speelgerechtigdheid kon niet aangepast worden.');
        } finally {
            setSavingEligibility(false);
            setEligibilityPlayer(null);
        }
    };

    const handleSavePhotoUrl = async () => {
        if (!editingPlayer || newPhotoUrl === editingPlayer.photoUrl) {
            setPhotoOpen(false);
            return;
        }
        try {
            await updateDoc(seasonPlayerDoc(seasonId, editingPlayer.id), { photoUrl: newPhotoUrl });

            const bijwerken = (p) => (p.id === editingPlayer.id ? { ...p, photoUrl: newPhotoUrl } : p);
            setSquad((huidig) => huidig.map(bijwerken));
            setPlayers((huidig) => huidig.map(bijwerken));
            notify.success(`De foto van ${editingPlayer.name} is bijgewerkt.`);
        } catch (err) {
            console.error('Kon de foto niet bijwerken:', err);
            notify.error('De foto kon niet bijgewerkt worden.');
        } finally {
            setPhotoOpen(false);
            setEditingPlayer(null);
            setNewPhotoUrl('');
        }
    };

    const handleCoachSwap = async () => {
        if (!coachIn || !coachOut || coachIn.id === coachOut.id) {
            notify.error('Kies twee verschillende coaches.');
            return;
        }

        setSwappingCoach(true);
        try {
            const executeCoachSwap = httpsCallable(functions, 'executeCoachSwap');
            const result = await executeCoachSwap({
                coachOutId: coachOut.id, coachInId: coachIn.id,
                coachOutData: coachOut, coachInData: coachIn,
                seasonId,
            });
            notify.success(result.data.message);
            setCoachOut(null);
            setCoachIn(null);
            await loadClubsAndPlayers();
        } catch (err) {
            console.error('Coach wisselen mislukt:', err);
            notify.error(err.message || 'De coach kon niet gewisseld worden.');
        } finally {
            setSwappingCoach(false);
            setCoachSwapOpen(false);
        }
    };

    const bezig = syncing || removing || aggregating;

    return (
        <Box>
            <SeasonBreadcrumb page="Stamgegevens" />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Box>
                    <Typography variant="h4">Stamgegevens</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {clubs.length} clubs · {totaalSpelers} spelers
                        {laatstBijgewerkt ? ` · bijgewerkt ${formatUpdated(laatstBijgewerkt)}` : ''}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={aggregating ? <CircularProgress size={20} color="inherit" /> : <HistoryIcon />}
                        onClick={handleAggregateHistory}
                        disabled={bezig || clubs.length === 0}
                    >
                        {aggregating ? 'Opslaan…' : 'Seizoenstotalen opslaan'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => setCoachSwapOpen(true)}
                        disabled={bezig}
                    >
                        Coach wisselen
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => { setAddClubError(null); setAddClubOpen(true); }}
                        disabled={bezig || isLocked}
                    >
                        Club toevoegen
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        onClick={handleSync}
                        disabled={bezig || isLocked || clubs.length === 0}
                    >
                        {syncing ? 'Bezig…' : 'Synchroniseren'}
                    </Button>
                </Box>
            </Box>

            {isLocked && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Dit seizoen is afgerond. Synchroniseren zou de selecties van vandaag onder een
                    oud seizoen zetten, dus dat is uitgeschakeld.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Zoek club of speler"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, minWidth: 240 }}
                />
                <FormControlLabel
                    control={<Checkbox checked={filterInAuction} onChange={(e) => setFilterInAuction(e.target.checked)} />}
                    label="Alleen spelers in een veiling"
                />
                {selectedClubs.size > 0 && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            {selectedClubs.size} geselecteerd
                        </Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={removing ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                            onClick={() => setConfirmRemoveOpen(true)}
                            disabled={bezig}
                        >
                            Uit seizoen
                        </Button>
                    </>
                )}
            </Box>

            {loadingClubs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : clubs.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Nog geen clubs in dit seizoen.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddClubOpen(true)} disabled={isLocked}>
                        Club toevoegen
                    </Button>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedClubs.size > 0 && selectedClubs.size === zichtbareClubs.length}
                                        indeterminate={selectedClubs.size > 0 && selectedClubs.size < zichtbareClubs.length}
                                        onChange={(e) => setSelectedClubs(e.target.checked
                                            ? new Set(zichtbareClubs.map((c) => c.id))
                                            : new Set())}
                                    />
                                </TableCell>
                                <TableCell>Club</TableCell>
                                <TableCell>Coach</TableCell>
                                <TableCell>Selectie</TableCell>
                                <TableCell>Bijgewerkt</TableCell>
                                <TableCell sx={{ width: 40 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {zichtbareClubs.map((club) => {
                                const expanded = expandedClubId === club.id;
                                const shownSquad = filterInAuction
                                    ? squad.filter((p) => inAuctionIds.has(String(p.id)))
                                    : squad;
                                return (
                                <React.Fragment key={club.id}>
                                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleSquad(club)}>
                                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={selectedClubs.has(club.id)}
                                            onChange={() => toggleClub(club.id)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar src={club.logoUrl} sx={{ width: 28, height: 28 }} />
                                            <Typography variant="body2">{club.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color={club.coach ? 'text.primary' : 'text.secondary'}>
                                            {club.coach || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {club.totaal === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                nog niet gesynchroniseerd
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                                <Chip size="small" variant="outlined" label={`${club.keepers} K`} />
                                                <Chip size="small" variant="outlined" label={`${club.verdedigers} V`} />
                                                <Chip size="small" variant="outlined" label={`${club.middenvelders} M`} />
                                                <Chip size="small" variant="outlined" label={`${club.aanvallers} A`} />
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatUpdated(club.lastUpdated)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>
                                        <ChevronRightIcon
                                            fontSize="small"
                                            sx={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                                                {loadingStats ? (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                                                ) : shownSquad.length === 0 ? (
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                        {filterInAuction
                                                            ? 'Geen spelers uit deze club in een veiling.'
                                                            : 'Deze club heeft nog geen selectie. Klik op Synchroniseren om hem op te halen.'}
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                                            Selectie · {shownSquad.length} spelers
                                                        </Typography>
                                                        <TableContainer component={Paper}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Speler</TableCell>
                                                                        <TableCell>Positie</TableCell>
                                                                        <TableCell align="center">In veiling</TableCell>
                                                                        <TableCell align="right">Wedstrijden</TableCell>
                                                                        <TableCell align="right">Goals</TableCell>
                                                                        <TableCell align="right">Assists</TableCell>
                                                                        <TableCell align="center">Acties</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {shownSquad.map((player) => (
                                                                        <TableRow
                                                                            key={player.id}
                                                                            sx={isNotEligiblePlayer(player) ? { bgcolor: 'action.hover' } : undefined}
                                                                        >
                                                                            <TableCell>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                                    <Avatar
                                                                                        src={player.photoUrl}
                                                                                        onClick={() => {
                                                                                            setEditingPlayer(player);
                                                                                            setNewPhotoUrl(player.photoUrl || '');
                                                                                            setPhotoOpen(true);
                                                                                        }}
                                                                                        sx={{
                                                                                            cursor: 'pointer',
                                                                                            ...(isNotEligiblePlayer(player) && { filter: 'grayscale(1)', opacity: 0.6 }),
                                                                                        }}
                                                                                    />
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        color={isNotEligiblePlayer(player) ? 'text.secondary' : 'text.primary'}
                                                                                        sx={isNotEligiblePlayer(player) ? { textDecoration: 'line-through' } : undefined}
                                                                                    >
                                                                                        {player.name}
                                                                                    </Typography>
                                                                                    {isNotEligiblePlayer(player) && (
                                                                                        <Chip size="small" color="error" icon={<BlockIcon />} label="Niet speelgerechtigd" />
                                                                                    )}
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell>{player.position}</TableCell>
                                                                            <TableCell align="center">
                                                                                {inAuctionIds.has(String(player.id)) && (
                                                                                    <CheckIcon fontSize="small" color="success" aria-label="In veiling" />
                                                                                )}
                                                                            </TableCell>
                                                                            {['matches', 'goals', 'assists'].map((veld) => (
                                                                                <TableCell align="right" key={veld}>
                                                                                    <TextField
                                                                                        type="number"
                                                                                        size="small"
                                                                                        sx={{ width: '80px' }}
                                                                                        value={playerStats[player.id]?.[veld] ?? ''}
                                                                                        onChange={(e) => handleStatChange(String(player.id), veld, e.target.value)}
                                                                                    />
                                                                                </TableCell>
                                                                            ))}
                                                                            <TableCell align="center">
                                                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                                                    <Tooltip title={isNotEligiblePlayer(player) ? 'Weer speelgerechtigd maken' : 'Niet speelgerechtigd maken'}>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            color={isNotEligiblePlayer(player) ? 'error' : 'default'}
                                                                                            aria-label={isNotEligiblePlayer(player) ? 'Weer speelgerechtigd maken' : 'Niet speelgerechtigd maken'}
                                                                                            onClick={() => setEligibilityPlayer(player)}
                                                                                        >
                                                                                            <BlockIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                    <IconButton size="small" aria-label="Verwijderen" onClick={() => setPlayerToDelete(player)}>
                                                                                        <DeleteIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        aria-label="Zoeken op Google"
                                                                                        onClick={() => window.open(
                                                                                            `https://www.google.com/search?q=${encodeURIComponent(`Statistieken ${player.name} ${player.clubName}`)}`,
                                                                                            '_blank', 'noopener,noreferrer',
                                                                                        )}
                                                                                    >
                                                                                        <SearchIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </Box>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                                </React.Fragment>
                                );
                            })}
                            {zichtbareClubs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                            Geen club of speler gevonden voor &ldquo;{search}&rdquo;.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={Boolean(positionChanges)} onClose={() => setPositionChanges(null)} fullWidth maxWidth="md">
                <DialogTitle>Gewijzigde posities</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Sportmonks geeft bij {positionChanges?.length} speler(s) een andere positie op dan
                        in de stamgegevens staat. Vink aan welke wijzigingen je wilt doorvoeren. Spelers
                        zonder vinkje houden hun huidige positie.
                    </DialogContentText>
                    <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={acceptedChanges.size > 0 && acceptedChanges.size === positionChanges?.length}
                                            indeterminate={acceptedChanges.size > 0 && acceptedChanges.size < (positionChanges?.length || 0)}
                                            onChange={(e) => setAcceptedChanges(e.target.checked
                                                ? new Set(positionChanges.map((c) => c.playerId))
                                                : new Set())}
                                        />
                                    </TableCell>
                                    <TableCell>Speler</TableCell>
                                    <TableCell>Club</TableCell>
                                    <TableCell>Huidige positie</TableCell>
                                    <TableCell>Nieuwe positie</TableCell>
                                    <TableCell align="center">In veiling</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(positionChanges || []).map((change) => (
                                    <TableRow
                                        key={change.playerId}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => toggleAcceptedChange(change.playerId)}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={acceptedChanges.has(change.playerId)} />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar src={change.photoUrl} sx={{ width: 28, height: 28 }} />
                                                <Typography variant="body2">{change.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{change.clubName}</TableCell>
                                        <TableCell>{change.currentPosition}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color={acceptedChanges.has(change.playerId) ? 'text.primary' : 'text.secondary'}
                                            >
                                                {change.newPosition}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {inAuctionIds.has(String(change.playerId)) && (
                                                <CheckIcon fontSize="small" color="success" aria-label="In veiling" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPositionChanges(null)}>Annuleren</Button>
                    <Button variant="contained" onClick={handleConfirmPositionChanges}>
                        Synchroniseren ({acceptedChanges.size} positie{acceptedChanges.size === 1 ? '' : 's'} wijzigen)
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addClubOpen} onClose={() => !addingClub && setAddClubOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Club toevoegen</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 3 }}>
                        De club wordt toegevoegd aan {seasonName}, samen met zijn actuele selectie en coach.
                    </DialogContentText>
                    {addClubError && <Alert severity="error" sx={{ mb: 2 }}>{addClubError}</Alert>}
                    <Autocomplete
                        options={sportmonksTeams}
                        onOpen={fetchSportmonksTeams}
                        loading={loadingTeams}
                        value={selectedTeam}
                        onChange={(event, value) => setSelectedTeam(value)}
                        getOptionLabel={(option) => `${option.name}${option.country?.name ? ` (${option.country.name})` : ''}`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Zoek een club"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingTeams ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddClubOpen(false)} disabled={addingClub}>Annuleren</Button>
                    <Button variant="contained" onClick={handleAddClub} disabled={addingClub || !selectedTeam}>
                        {addingClub ? 'Bezig…' : 'Toevoegen'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)}>
                <DialogTitle>Uit dit seizoen verwijderen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {selectedClubs.size} club(s) en al hun spelers worden uit {seasonName} verwijderd.
                        Andere seizoenen blijven ongemoeid.
                        <br /><br />
                        Deze actie kan niet ongedaan worden gemaakt.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRemoveOpen(false)}>Annuleren</Button>
                    <Button onClick={handleRemoveClubs} color="error" autoFocus>Verwijderen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(playerToDelete)} onClose={() => setPlayerToDelete(null)}>
                <DialogTitle>Speler verwijderen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je <strong>{playerToDelete?.name}</strong> uit dit seizoen wilt
                        verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPlayerToDelete(null)}>Annuleren</Button>
                    <Button onClick={handleDeletePlayer} color="error" autoFocus>Verwijderen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(eligibilityPlayer)} onClose={() => !savingEligibility && setEligibilityPlayer(null)}>
                <DialogTitle>
                    {isNotEligiblePlayer(eligibilityPlayer) ? 'Weer speelgerechtigd maken' : 'Niet speelgerechtigd maken'}
                </DialogTitle>
                <DialogContent>
                    {isNotEligiblePlayer(eligibilityPlayer) ? (
                        <DialogContentText>
                            <strong>{eligibilityPlayer?.name}</strong> komt weer op de transfermarkt. Deelnemers
                            bij wie hij uit het team is gehaald, krijgen hem niet vanzelf terug.
                        </DialogContentText>
                    ) : (
                        <DialogContentText>
                            <strong>{eligibilityPlayer?.name}</strong> verdwijnt van de transfermarkt. Zit hij
                            in {seasonName} al in het team van een deelnemer, dan gaat hij daar uit met een
                            transfer "Niet speelgerechtigd" en krijgt die deelnemer een extra transfer om
                            zijn team weer compleet te maken.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEligibilityPlayer(null)} disabled={savingEligibility}>Annuleren</Button>
                    <Button
                        onClick={handleToggleEligibility}
                        color={isNotEligiblePlayer(eligibilityPlayer) ? 'primary' : 'error'}
                        variant="contained"
                        disabled={savingEligibility}
                        autoFocus
                    >
                        {savingEligibility ? 'Bezig…' : 'Bevestigen'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={photoOpen} onClose={() => setPhotoOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Foto bewerken</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Foto-URL voor <strong>{editingPlayer?.name}</strong>.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Foto-URL"
                        fullWidth
                        value={newPhotoUrl}
                        onChange={(e) => setNewPhotoUrl(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPhotoOpen(false)}>Annuleren</Button>
                    <Button variant="contained" onClick={handleSavePhotoUrl}>Opslaan</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={coachSwapOpen} onClose={() => !swappingCoach && setCoachSwapOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Coach wisselen</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 3 }}>
                        Deelnemers in {seasonName} die de vertrekkende coach in hun team hebben,
                        krijgen zijn opvolger ervoor terug. De stamgegevens veranderen hier niet van —
                        die haal je op met Synchroniseren.
                    </DialogContentText>
                    {coaches.length < 2 && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Er staan {coaches.length === 0 ? 'nog geen coaches' : 'nog maar één coach'} in
                            dit seizoen. Voeg eerst clubs toe of synchroniseer, dan komen hun coaches mee.
                        </Alert>
                    )}
                    <Autocomplete
                        options={coaches}
                        value={coachOut}
                        onChange={(event, value) => setCoachOut(value)}
                        getOptionLabel={(option) => `${option.name} (${option.clubName})`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => <TextField {...params} label="Vertrekkende coach" />}
                        sx={{ mb: 3 }}
                    />
                    <Autocomplete
                        options={coaches}
                        value={coachIn}
                        onChange={(event, value) => setCoachIn(value)}
                        getOptionLabel={(option) => `${option.name} (${option.clubName})`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => <TextField {...params} label="Nieuwe coach" />}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCoachSwapOpen(false)} disabled={swappingCoach}>Annuleren</Button>
                    <Button
                        variant="contained"
                        onClick={handleCoachSwap}
                        disabled={swappingCoach || !coachIn || !coachOut}
                    >
                        {swappingCoach ? 'Bezig…' : 'Wisselen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminStamgegevens;
